'use client';

import { signInAction } from '@/actions/auth/sign-in-action';
import { CBJRImage, useCBJR } from '@/components/cbjr'; // Importing the custom hook
import { type SignInInput, signInSchema } from '@/schemas/sign-in-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@vibe/design-system/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@vibe/design-system/ui/form';
import { Input } from '@vibe/design-system/ui/input';
import { PasswordInput } from '@vibe/design-system/vibe/password-input';
import { AlertCircle, Loader } from 'lucide-react';
import { useAction } from 'next-safe-action/hooks';
import { type FieldValues, type UseFormReturn, useForm } from 'react-hook-form';

export function SignInForm() {
  const { execute, isPending, result } = useAction(signInAction);

  const form = useForm<SignInInput>({
    resolver: zodResolver(signInSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      remember: false,
    },
  });

  const onSubmit = (data: SignInInput) => {
    execute(data);
  };

  const { showCBJR, setShowCBJR } = useCBJR(
    form as unknown as UseFormReturn<SignInInput>,
    onSubmit as (data: FieldValues) => void
  );

  return (
    <Form {...form}>
      <CBJRImage showCBJR={showCBJR} setShowCBJR={setShowCBJR} />
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          {/* Email Field */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="exemplo@email.com"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Password Field */}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Senha</FormLabel>
                <FormControl>
                  <PasswordInput placeholder="Informe a senha" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {result?.data?.error && (
          <div className="rounded-md bg-destructive/15 p-3 text-center text-sm text-destructive">
            <div className="flex items-center justify-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <span>{result.data.error}</span>
            </div>
          </div>
        )}

        <div className="relative">
          <Button
            type="submit"
            className="h-12 w-full font-medium text-base relative z-10"
            disabled={isPending || !form.formState.isValid}
          >
            {isPending ? (
              <div className="flex items-center gap-2">
                <Loader className="h-4 w-4 animate-spin" />
                <span>Aguarde...</span>
              </div>
            ) : (
              'Continuar'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
